/** Member hall hours and week-grid helpers for the player new-session dialog. */

export const MEMBER_HOURS_START = 8;
export const MEMBER_HOURS_END = 22;
export const SLOT_MINUTES = 30;
export const MAX_SCHEDULE_WEEKS = 8;

export function startOfDay(date) {
  const d = date instanceof Date ? date : new Date(date);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Monday of the week containing `date` (local calendar). */
export function startOfWeekMonday(date = new Date()) {
  const d = startOfDay(date);
  const weekday = d.getDay();
  const diff = weekday === 0 ? -6 : 1 - weekday;
  d.setDate(d.getDate() + diff);
  return d;
}

export function addDays(date, days) {
  const d = startOfDay(date);
  d.setDate(d.getDate() + Number(days || 0));
  return d;
}

export function addWeeks(date, weeks) {
  return addDays(date, Number(weeks || 0) * 7);
}

export function toDateKey(date) {
  const d = date instanceof Date ? date : new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function parseDateKey(key) {
  const m = String(key || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

export function weekDays(weekStart) {
  const start = startOfWeekMonday(weekStart);
  return Array.from({ length: 7 }, (_, i) => addDays(start, i));
}

export function slotStartsForDay(day, durationMinutes = SLOT_MINUTES) {
  const duration = Math.max(SLOT_MINUTES, Number(durationMinutes) || SLOT_MINUTES);
  const lastStartMinutes = MEMBER_HOURS_END * 60 - duration;
  const out = [];
  for (
    let minutes = MEMBER_HOURS_START * 60;
    minutes <= lastStartMinutes;
    minutes += SLOT_MINUTES
  ) {
    const start = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 0, 0, 0, 0);
    start.setMinutes(minutes);
    out.push(start);
  }
  return out;
}

export function heatmapSlotsForDay(day) {
  return slotStartsForDay(day, SLOT_MINUTES);
}

export function slotEnd(start, durationMinutes) {
  const d = new Date(start instanceof Date ? start.getTime() : new Date(start).getTime());
  d.setMinutes(d.getMinutes() + Math.max(SLOT_MINUTES, Number(durationMinutes) || SLOT_MINUTES));
  return d;
}

export function intervalsOverlap(aStart, aEnd, bStart, bEnd) {
  const as = new Date(aStart).getTime();
  const ae = new Date(aEnd).getTime();
  const bs = new Date(bStart).getTime();
  const be = new Date(bEnd).getTime();
  if (![as, ae, bs, be].every(Number.isFinite)) return false;
  return as < be && ae > bs;
}

export function slotOccupancy({ occupancy = [], tableCount = 1, slotStart, slotEnd: end }) {
  const busy = new Set();
  for (const row of occupancy) {
    if (!row) continue;
    if (!intervalsOverlap(row.starts_at, row.ends_at, slotStart, end)) continue;
    if (row.table_id) busy.add(String(row.table_id));
  }
  const total = Math.max(1, Number(tableCount) || 1);
  const used = Math.min(total, busy.size);
  const free = Math.max(0, total - used);
  let status = "open";
  if (used >= total) status = "full";
  else if (used > 0) status = "mixed";
  return { used, total, free, status };
}

export function slotFitsMemberHours(start, durationMinutes = SLOT_MINUTES) {
  const s = start instanceof Date ? start : new Date(start);
  const e = slotEnd(s, durationMinutes);
  const startMin = s.getHours() * 60 + s.getMinutes();
  const endMin = e.getHours() * 60 + e.getMinutes() + (e.getDate() !== s.getDate() ? 24 * 60 : 0);
  return startMin >= MEMBER_HOURS_START * 60 && endMin <= MEMBER_HOURS_END * 60;
}

export function isSlotInPast(start, now = new Date(), graceMs = 2 * 60 * 1000) {
  return new Date(start).getTime() < now.getTime() - graceMs;
}

export function isSlotLiveNow(start, now = new Date(), windowMs = 15 * 60 * 1000) {
  const t = new Date(start).getTime();
  return Number.isFinite(t) && t <= now.getTime() + windowMs;
}

export function snapToNextSlot(now = new Date()) {
  const d = new Date(now.getTime());
  const minutes = d.getMinutes();
  const extra = minutes % SLOT_MINUTES === 0 ? 0 : SLOT_MINUTES - (minutes % SLOT_MINUTES);
  d.setSeconds(0, 0);
  d.setMinutes(minutes + extra);
  const dayMinutes = d.getHours() * 60 + d.getMinutes();
  if (dayMinutes < MEMBER_HOURS_START * 60) {
    d.setHours(MEMBER_HOURS_START, 0, 0, 0);
  }
  if (dayMinutes >= MEMBER_HOURS_END * 60) {
    const next = addDays(d, 1);
    next.setHours(MEMBER_HOURS_START, 0, 0, 0);
    return next;
  }
  return d;
}

/** Next 1h (or duration) member slot from now that is not fully booked. */
export function nextOpenMemberSlot({
  occupancy = [],
  tableCount = 1,
  durationMinutes = 60,
  now = new Date(),
} = {}) {
  let start = snapToNextSlot(now);
  for (let i = 0; i < 96; i++) {
    if (!slotFitsMemberHours(start, durationMinutes) || isSlotInPast(start, now)) {
      const day = startOfDay(start);
      const tomorrow = addDays(day, 1);
      tomorrow.setHours(MEMBER_HOURS_START, 0, 0, 0);
      start = tomorrow;
      continue;
    }
    const occ = slotOccupancy({
      occupancy,
      tableCount,
      slotStart: start,
      slotEnd: slotEnd(start, durationMinutes),
    });
    if (occ.status !== "full") return start;
    start = new Date(start.getTime() + SLOT_MINUTES * 60 * 1000);
  }
  return snapToNextSlot(now);
}

export function formatWeekRange(weekStart, locale) {
  const start = startOfWeekMonday(weekStart);
  const end = addDays(start, 6);
  const opts = { day: "numeric", month: "short" };
  const startLabel = start.toLocaleDateString(locale, opts);
  const endLabel = end.toLocaleDateString(locale, {
    ...opts,
    year: start.getFullYear() === end.getFullYear() ? "numeric" : undefined,
  });
  const year =
    start.getFullYear() === end.getFullYear() ? "" : ` ${start.getFullYear()}`;
  return `${startLabel}${year} – ${endLabel}`;
}

export function formatSlotTime(date, locale) {
  return new Date(date).toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
  });
}
